// Shared Constants: Müşteri Modülü Enum'ları
// Bu dosya `sync-shared.js` ile frontend/src/shared/ ve backend/src/shared/
// dizinlerine kopyalanır. ⚠️ Kopyaları düzenleme, burayı düzenle.
//
// Kural (rota 2.2'de konuldu, 2.3'te müşteri modülüne yayıldı):
//   **değer kümesi burada, etiket ve renk `constants/index.ts`'te.**
//   Açılır liste seçenekleri kümeden TÜRETİLİR, elle yazılmaz.
//
// Buradaki her küme bir DB ENUM'unun birebir karşılığıdır; sıra da ENUM sırasıdır,
// çünkü açılır listeler bu sıradan üretiliyor. Yeni bir değer eklemek iki adımlıdır:
// önce migration (ENUM'a ALTER TYPE ... ADD VALUE), sonra buraya satır. Etiket
// yazılmazsa `tsc` kırmızıya döner (`Record<XType, string>`).

// ─── Müşteri türü (musteriler.musteri_turu → musteri_turu_enum) ───
// BAYI_ALTI'yı kullanıcı elle de seçebilir ama asıl kaynağı bayilik grubudur:
// gruba eklenen müşteriyi `customerService` otomatik BAYI_ALTI yapar.
export const MUSTERI_TURU = {
    MEVCUT: 'MEVCUT',
    POTANSIYEL: 'POTANSIYEL',
    BAYI_ALTI: 'BAYI_ALTI'
} as const;

// Müşteri formunda tür seçilmezse DB DEFAULT'u budur (`musteriler` tablosu).
// Frontend aynı varsayılanı gösterdiği için tek yerden okunur.
export const VARSAYILAN_MUSTERI_TURU = MUSTERI_TURU.POTANSIYEL;

// ─── Müşteri ilk kaynağı (musteriler.ilk_kaynak → musteri_kaynak_enum) ───
// SAAS-COUPLING: ilk dört değer TrioCare'in bağlı olduğu pazaryerlerinden,
// kalan dokuz değer TrioCare'in satış kanallarından türüyor — hardcoded —
// extraction'da tenant'ın kanal tanımından beslenmeli.
//
// DİKKAT: `siparis_kaynak` (mağaza kimliği, `sources.ts`) ile AYNI DEĞİL.
// Orada mağaza ayrımı var (SHOPIFY_PROBED / SHOPIFY_TRIOCARE), burada yok:
// müşterinin nereden geldiği mağaza değil kanal sorusudur.
export const MUSTERI_KAYNAK = {
    SHOPIFY: 'SHOPIFY',
    TRENDYOL: 'TRENDYOL',
    HEPSIBURADA: 'HEPSIBURADA',
    HARICI: 'HARICI',
    SCRAPING: 'SCRAPING',
    FUAR: 'FUAR',
    B2B_SAHA: 'B2B_SAHA',
    REFERANS: 'REFERANS',
    WHATSAPP: 'WHATSAPP',
    INSTAGRAM: 'INSTAGRAM',
    FACEBOOK: 'FACEBOOK',
    GOOGLE_ADS: 'GOOGLE_ADS',
    LINKEDIN: 'LINKEDIN'
} as const;

// ─── Lead durumu (musteriler.lead_durum → lead_durum_enum) ───
// ONAYLANDI'yı sipariş akışı da yazar: teklif siparişe dönüşünce
// (`orderQuotationService`) ve satış siparişi açılınca (`orderCrudService`).
export const LEAD_DURUM = {
    YENI: 'YENI',
    ILETISIME_GECILDI: 'ILETISIME_GECILDI',
    ILGILI: 'ILGILI',
    TEKLIF_VERILDI: 'TEKLIF_VERILDI',
    ONAYLANDI: 'ONAYLANDI',
    REDDEDILDI: 'REDDEDILDI',
    TAKIPTE: 'TAKIPTE'
} as const;

// Müşteri formunda lead durumu seçilmezse DB DEFAULT'u budur.
export const VARSAYILAN_LEAD_DURUM = LEAD_DURUM.YENI;

// ─── Aktivite türü (musteri_aktiviteleri.aktivite_turu → aktivite_turu_enum) ───
export const AKTIVITE_TURU = {
    ARAMA: 'ARAMA',
    WHATSAPP: 'WHATSAPP',
    WHATSAPP_KONUSMA: 'WHATSAPP_KONUSMA',
    EMAIL: 'EMAIL',
    ZIYARET: 'ZIYARET',
    SUNUM: 'SUNUM',
    NUMUNE_GONDERIMI: 'NUMUNE_GONDERIMI',
    NOT: 'NOT',
    SISTEM: 'SISTEM'
} as const;

// ─── Aktivite sonucu (musteri_aktiviteleri.sonuc → aktivite_sonuc_enum) ───
// IPTAL yalnız takip kapatma akışında seçilebilir (aşağıdaki alt küme), ama
// ENUM'un parçasıdır ve canlıda kayıtlıdır — etiketi olmak ZORUNDA.
export const AKTIVITE_SONUC = {
    CEVAP_YOK: 'CEVAP_YOK',
    ILGILI: 'ILGILI',
    ILGISIZ: 'ILGISIZ',
    TEKRAR_ARANACAK: 'TEKRAR_ARANACAK',
    TEKLIF_VERILDI: 'TEKLIF_VERILDI',
    SIPARIS_ALINDI: 'SIPARIS_ALINDI',
    IPTAL: 'IPTAL'
} as const;

// ─── Aktivite kaynağı (musteri_aktiviteleri.kaynak → aktivite_kaynak_enum) ───
// Yalnız MANUEL kayıtlar düzenlenip silinebilir; OTOMATIK olanları sistem yazar.
// DİKKAT: `urun_kanal_fiyat_gecmis.kaynak` de MANUEL içerir ama o AYRI bir küme
// (MANUEL / WEBHOOK / TOPLU, ENUM değil CHECK) — bu sabitle karıştırılmamalı.
export const AKTIVITE_KAYNAK = {
    MANUEL: 'MANUEL',
    OTOMATIK: 'OTOMATIK'
} as const;

// ─── Eşleştirme önerisi durumu (musteri_eslestirme_onerileri.durum) ───
export const ESLESTIRME_DURUM = {
    BEKLIYOR: 'BEKLIYOR',
    ONAYLANDI: 'ONAYLANDI',
    REDDEDILDI: 'REDDEDILDI'
} as const;

// ─── Sektör (musteriler.sektor) ───
// ⚠️ Buradaki tek küme DB ENUM'u DEĞİL: kolon `character varying(100)`, serbest metin.
// Kale yok, yani değeri yalnız uygulama koruyor.
//
// Buraya rota 3.2'de taşındı. Önceki gerekçe "değeri yalnız arayüzün açılır listesi
// yazıyor, ikinci yazan taraf yok" idi; backend doğrulaması eklenince küme İKİ
// tüketicili oldu (Joi şeması + açılır liste) ve tek kaynak şartı devreye girdi.
// Aynı ayrım `IADE_NEDENLERI` ve `KARGO_FIRMALARI` için de geçerlidir (rota 2.2).
//
// SAAS-COUPLING: liste TrioCare'in hedef müşteri segmentlerinden türüyor
// (veteriner, dövme, pet kuaför...) — hardcoded — extraction'da tenant'ın kendi
// sektör tanımına taşınmalı, sektör eklemek kod değil veri işi olmalı.
export const SEKTOR = {
    VETERINER: 'VETERINER',
    PET_KUAFOR: 'PET_KUAFOR',
    DOVME: 'DOVME',
    HASTANE: 'HASTANE',
    SPA: 'SPA',
    MASAJ_SALONU: 'MASAJ_SALONU',
    MEDIKAL: 'MEDIKAL'
} as const;

// ─── Türetilmiş listeler ───
export const ALL_MUSTERI_TURU = Object.values(MUSTERI_TURU);
export const ALL_MUSTERI_KAYNAK = Object.values(MUSTERI_KAYNAK);
export const ALL_LEAD_DURUM = Object.values(LEAD_DURUM);
export const ALL_AKTIVITE_TURU = Object.values(AKTIVITE_TURU);
export const ALL_AKTIVITE_SONUC = Object.values(AKTIVITE_SONUC);
export const ALL_AKTIVITE_KAYNAK = Object.values(AKTIVITE_KAYNAK);
export const ALL_ESLESTIRME_DURUM = Object.values(ESLESTIRME_DURUM);
export const ALL_SEKTOR = Object.values(SEKTOR);

// Kullanıcının elle girebileceği aktivite türleri — SISTEM hariç.
// Hem aktivite formu/filtre çipleri hem backend Joi doğrulaması bunu kullanır;
// eskiden iki ayrı elle yazılmış listeydi.
export const SECILEBILIR_AKTIVITE_TURLERI = ALL_AKTIVITE_TURU.filter((v) => v !== AKTIVITE_TURU.SISTEM);

// Normal aktivite kaydında seçilebilen sonuçlar — IPTAL hariç.
// IPTAL "takibi bırak" anlamına gelir ve YALNIZ takip kapatma penceresinde
// sunulur; sıradan bir aktiviteye iliştirilmesi anlamsız olurdu.
// Backend `closeFollowUpSchema` bu alt kümeyi değil TAMAMINI (`ALL_AKTIVITE_SONUC`)
// kabul eder — kapatma akışı IPTAL yazabilmeli.
export const SECILEBILIR_AKTIVITE_SONUCLARI = ALL_AKTIVITE_SONUC.filter((v) => v !== AKTIVITE_SONUC.IPTAL);

// ─── TypeScript tipleri ───
export type MusteriTuru = typeof ALL_MUSTERI_TURU[number];
export type MusteriKaynak = typeof ALL_MUSTERI_KAYNAK[number];
export type LeadDurum = typeof ALL_LEAD_DURUM[number];
export type AktiviteTuru = typeof ALL_AKTIVITE_TURU[number];
export type AktiviteSonuc = typeof ALL_AKTIVITE_SONUC[number];
export type AktiviteKaynak = typeof ALL_AKTIVITE_KAYNAK[number];
export type EslestirmeDurum = typeof ALL_ESLESTIRME_DURUM[number];
export type Sektor = typeof ALL_SEKTOR[number];
